/**
 * A PF ticket's lines (GET /pipeline-facility/tickets/:id → lines).
 *
 * Unpriced lines are FLAGGED on the row — the office approve refuses while any
 * remain (`ticket_unpriced`). Adding, editing and deleting a line is offered
 * only while the ticket is editable (`editable`): a draft, or a signed ticket
 * the office has chosen to unlock (the screen asked first; the edit returns it
 * to draft). A line is priced from the rate card (`rate_id`) or by the office's
 * own figure (`rate`), never both. A drafted ('auto') line keeps its unit.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAddPfTicketLine, useRemovePfTicketLine, useUpdatePfTicketLine, type PfTicketEditResult } from '../../../hooks/usePfTickets';
import { fmtCurrency } from '../../../lib/display-labels';
import { pfTicketRefusal } from '../../../lib/pf-tickets';
import { fmtNumber, pfDay, toInput } from '../../../lib/pipeline-facility';
import type { PfCatalog } from '../../../types/pipeline-facility';
import type { PfRate, PfTicket, PfTicketLine, PfTicketLineCreate, PfTicketLinePatch } from '../../../types/pf-tickets';
import { PF_DATE_INPUT, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfEmpty, PfField, PfFormError, PfPanel } from '../PfBits';
import { PfInlineConfirm } from '../digs/DigBits';
import { pfRateOption, pfRateText, ratesForTicket } from './TicketBits';

type PriceMode = 'keep' | 'card' | 'office' | 'clear';

/** The line form: add (no `line`) or edit. Sends only what changed on an edit. */
export function PfTicketLineForm({
  ticket,
  line,
  rates,
  catalog,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  ticket: PfTicket;
  line?: PfTicketLine;
  rates: PfRate[];
  catalog: PfCatalog | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: PfTicketLineCreate | PfTicketLinePatch) => void;
}) {
  const adding = !line;
  const [description, setDescription] = useState(line?.description ?? '');
  const [quantity, setQuantity] = useState(toInput(line?.quantity));
  const [kind, setKind] = useState(line?.kind ?? '');
  const [unit, setUnit] = useState(line?.unit ?? '');
  const [mode, setMode] = useState<PriceMode>(adding ? 'card' : 'keep');
  const [rateId, setRateId] = useState('');
  const [rate, setRate] = useState('');
  const [workDate, setWorkDate] = useState(ticket.scope === 'day' ? ticket.workDate ?? '' : '');
  const [problem, setProblem] = useState<string | null>(null);
  const unitFixed = !!line && line.source !== 'manual';
  const effUnit = unit || null;
  const cardRates = ratesForTicket(rates, ticket, { unit: adding ? null : effUnit, on: ticket.workDate });
  const kinds = catalog?.priceKinds ?? [];
  const units = catalog?.priceUnits ?? [];

  const submit = () => {
    setProblem(null);
    if (!description.trim()) return setProblem('Enter what the line is for.');
    if (!quantity.trim() || !Number.isFinite(Number(quantity))) return setProblem('Enter the quantity as a number.');
    if (mode === 'card' && !rateId) return setProblem('Pick a rate from the rate card, or enter the office’s own rate.');
    if (mode === 'office' && (!rate.trim() || !Number.isFinite(Number(rate)))) return setProblem('Enter the rate as a number.');
    if (adding) {
      if (mode !== 'card' && (!kind || !unit)) return setProblem('Pick what kind of charge it is and its unit.');
      const body: PfTicketLineCreate = { description: description.trim(), quantity: quantity.trim() };
      if (mode === 'card') body.rate_id = rateId;
      else {
        body.kind = kind;
        body.unit = unit;
        if (mode === 'office') body.rate = rate.trim();
      }
      if (ticket.scope === 'job' && workDate) body.work_date = workDate;
      return onSubmit(body);
    }
    const body: PfTicketLinePatch = {};
    if (description.trim() !== line!.description) body.description = description.trim();
    if (Number(quantity) !== line!.quantity) body.quantity = quantity.trim();
    if (!unitFixed && unit && unit !== line!.unit) body.unit = unit;
    if (mode === 'card') body.rate_id = rateId;
    if (mode === 'office') body.rate = rate.trim();
    if (mode === 'clear') body.rate = null;
    if (!Object.keys(body).length) return setProblem('Nothing has changed.');
    return onSubmit(body);
  };

  return (
    <div data-testid={adding ? 'pf-ticket-line-add-form' : 'pf-ticket-line-edit-form'} className="space-y-3 border-t border-fieldiq-border bg-fieldiq-dark/40 px-4 py-4">
      <PfFormError message={problem ?? error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <PfField label="Description *">
            <input className={PF_INPUT} value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
          </PfField>
        </div>
        <PfField label="Quantity *">
          <input className={PF_INPUT} inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </PfField>
        {adding && ticket.scope === 'job' && (
          <PfField label="Work day" hint="Optional.">
            <input className={PF_DATE_INPUT} type="date" value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
          </PfField>
        )}
      </div>
      <fieldset className="space-y-2">
        <legend className="mb-1 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Price</legend>
        <div className="flex flex-wrap gap-4 text-sm text-fieldiq-text">
          {!adding && (
            <label className="flex items-center gap-2">
              <input type="radio" name="pf-price" checked={mode === 'keep'} onChange={() => setMode('keep')} /> Keep the price
            </label>
          )}
          <label className="flex items-center gap-2">
            <input type="radio" name="pf-price" checked={mode === 'card'} onChange={() => setMode('card')} /> From the rate card
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="pf-price" checked={mode === 'office'} onChange={() => setMode('office')} /> The office’s own rate
          </label>
          {!adding ? (
            <label className="flex items-center gap-2">
              <input type="radio" name="pf-price" checked={mode === 'clear'} onChange={() => setMode('clear')} /> Clear the price
            </label>
          ) : (
            <label className="flex items-center gap-2">
              <input type="radio" name="pf-price" checked={mode === 'clear'} onChange={() => setMode('clear')} /> No price yet
            </label>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {mode === 'card' && (
            <div className="sm:col-span-2">
              <PfField label="Rate" hint={adding ? 'The rate sets the kind and unit.' : 'Rates counted in this line’s unit.'}>
                <select className={PF_INPUT} aria-label="Rate" value={rateId} onChange={(e) => setRateId(e.target.value)}>
                  <option value="">{cardRates.length ? 'Pick a rate' : 'No rate on the card fits this line'}</option>
                  {cardRates.map((r) => (
                    <option key={r.id} value={r.id}>
                      {pfRateOption(r)}
                    </option>
                  ))}
                </select>
              </PfField>
            </div>
          )}
          {mode === 'office' && (
            <PfField label={`Rate (${ticket.currency ?? '—'})`}>
              <input className={PF_INPUT} inputMode="decimal" aria-label="Office rate" value={rate} onChange={(e) => setRate(e.target.value)} />
            </PfField>
          )}
          {adding && mode !== 'card' && (
            <PfField label="Kind *">
              <select className={PF_INPUT} aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">Pick one</option>
                {kinds.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
            </PfField>
          )}
          {(adding ? mode !== 'card' : !unitFixed) && (
            <PfField label={adding ? 'Unit *' : 'Unit'}>
              <select className={PF_INPUT} aria-label="Unit" value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value="">Pick one</option>
                {units.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </PfField>
          )}
        </div>
        {unitFixed && <p className="text-xs text-fieldiq-muted">A line drafted from the logs keeps the unit its hours were counted in.</p>}
      </fieldset>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="button" className={PF_PRIMARY} onClick={submit} disabled={pending}>
          {pending ? 'Saving…' : adding ? 'Add line' : 'Save line'}
        </button>
      </div>
    </div>
  );
}

export function PfTicketLines({
  ticket,
  editable,
  canWrite,
  rates,
  catalog,
  highlight,
  onEdited,
}: {
  ticket: PfTicket;
  /** Draft, or a signed ticket the office unlocked. */
  editable: boolean;
  canWrite: boolean;
  rates: PfRate[];
  catalog: PfCatalog | null;
  /** Line ids an `ticket_unpriced` refusal named. */
  highlight: string[];
  onEdited: (r: PfTicketEditResult) => void;
}) {
  const add = useAddPfTicketLine();
  const update = useUpdatePfTicketLine();
  const remove = useRemovePfTicketLine();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lines = ticket.lines ?? [];
  const cur = ticket.currency;
  const write = editable && canWrite;
  const draft = ticket.status === 'draft';

  const done = (msg: string) => (r: PfTicketEditResult) => {
    toast.success(msg);
    setAdding(false);
    setEditingId(null);
    setDeletingId(null);
    onEdited(r);
  };

  return (
    <PfPanel
      eyebrow="Lines"
      title={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
      testId="pf-ticket-lines"
      actions={
        write && !adding ? (
          <button type="button" className={PF_SECONDARY} onClick={() => { setError(null); setEditingId(null); setAdding(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Add line
          </button>
        ) : undefined
      }
    >
      {(ticket.unpricedCount ?? 0) > 0 && (
        <p data-testid="pf-ticket-unpriced-count" className="border-b border-fieldiq-border bg-fi-signal-red/10 px-4 py-2 text-sm text-fieldiq-text">
          {ticket.unpricedCount === 1 ? 'One line has no rate.' : `${ticket.unpricedCount} lines have no rate.`} Price {ticket.unpricedCount === 1 ? 'it' : 'them'} or remove{' '}
          {ticket.unpricedCount === 1 ? 'it' : 'them'} before the office approves the ticket.
        </p>
      )}
      {adding && (
        <PfTicketLineForm
          ticket={ticket}
          rates={rates}
          catalog={catalog}
          pending={add.isPending}
          error={error}
          onCancel={() => setAdding(false)}
          onSubmit={(body) => {
            setError(null);
            add.mutate({ id: ticket.id, body: body as PfTicketLineCreate }, { onSuccess: done('Line added'), onError: (e) => setError(pfTicketRefusal(e, 'The line could not be added.')) });
          }}
        />
      )}
      {!lines.length ? (
        <PfEmpty>No lines. Refresh the ticket from the daily logs, or add a line.</PfEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-b border-fieldiq-border text-left text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                <th className="px-4 py-2">Line</th>
                <th className="px-4 py-2">Day</th>
                <th className="px-4 py-2 text-right">Quantity</th>
                <th className="px-4 py-2 text-right">Rate</th>
                <th className="px-4 py-2 text-right">Amount</th>
                {write && <th className="px-4 py-2 text-right"><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <React.Fragment key={l.id}>
                  <tr
                    data-testid="pf-ticket-line"
                    data-unpriced={l.unpriced ? 'true' : undefined}
                    className={`border-b border-fieldiq-border align-top ${l.unpriced ? 'bg-fi-signal-red/10' : ''} ${highlight.includes(l.id) ? 'outline outline-1 outline-fi-signal-red' : ''}`}
                  >
                    <td className="px-4 py-2">
                      <p className="font-semibold text-white">{l.description || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {l.kindLabel || l.kind || '—'}
                        {l.crewRoleLabel ? ` · ${l.crewRoleLabel}` : ''}
                        {l.standby ? ' · Standby' : ''} · {l.source === 'manual' ? 'Added by the office' : 'From the daily logs'}
                        {l.rateCode ? ` · ${l.rateCode}` : l.rateSource === 'office' ? ' · Office rate' : ''}
                      </p>
                      {l.unpriced && (
                        <p data-testid="pf-ticket-line-unpriced" className="mt-1 text-xs font-bold text-fi-signal-red">
                          No rate{l.unpricedReason ? ` — ${l.unpricedReason}` : ''}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2">{pfDay(l.workDate)}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {fmtNumber(l.quantity)} {l.unit === 'distance' ? l.distanceUnit ?? '' : l.unitLabel ?? l.unit}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{pfRateText(l.rate, cur, l.unit, l.distanceUnit)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtCurrency(l.amount, cur)}</td>
                    {write && (
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button type="button" aria-label={`Edit ${l.description}`} className={PF_SECONDARY} onClick={() => { setError(null); setAdding(false); setEditingId(l.id); }}>
                            <Pencil className="size-4" aria-hidden="true" />
                          </button>
                          <button type="button" aria-label={`Delete ${l.description}`} className={PF_SECONDARY} onClick={() => { setError(null); setDeletingId(l.id); }}>
                            <Trash2 className="size-4" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {write && deletingId === l.id && (
                    <tr>
                      <td colSpan={6} className="px-4 py-3">
                        <PfInlineConfirm
                          testId="pf-ticket-line-delete-confirm"
                          message={`Remove “${l.description}” from the ticket?${l.source === 'auto' ? ' A refresh from the daily logs will draw it again.' : ''}`}
                          confirmLabel="Remove line"
                          cancelLabel="Keep it"
                          pending={remove.isPending}
                          error={error}
                          onCancel={() => setDeletingId(null)}
                          onConfirm={() =>
                            remove.mutate({ id: ticket.id, lineId: l.id }, { onSuccess: done('Line removed'), onError: (e) => setError(pfTicketRefusal(e, 'The line could not be removed.')) })
                          }
                        />
                      </td>
                    </tr>
                  )}
                  {write && editingId === l.id && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <PfTicketLineForm
                          ticket={ticket}
                          line={l}
                          rates={rates}
                          catalog={catalog}
                          pending={update.isPending}
                          error={error}
                          onCancel={() => setEditingId(null)}
                          onSubmit={(body) =>
                            update.mutate(
                              { id: ticket.id, lineId: l.id, body: body as PfTicketLinePatch },
                              { onSuccess: done('Line saved'), onError: (e) => setError(pfTicketRefusal(e, 'The line could not be saved.')) },
                            )
                          }
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <dl data-testid="pf-ticket-totals" className="grid gap-1 border-t border-fieldiq-border px-4 py-3 text-sm sm:ml-auto sm:w-80">
        {draft ? (
          <TotalRow label="Subtotal (priced lines)" value={fmtCurrency(ticket.draftSubtotal, cur)} />
        ) : (
          <>
            <TotalRow label="Subtotal" value={fmtCurrency(ticket.subtotal, cur)} />
            {ticket.taxRate1Label && <TotalRow label={ticket.taxRate1Label} value={fmtCurrency(ticket.taxRate1Amount, cur)} />}
            {ticket.taxRate2Label && <TotalRow label={ticket.taxRate2Label} value={fmtCurrency(ticket.taxRate2Amount, cur)} />}
            <TotalRow label="Total" value={fmtCurrency(ticket.totalAmount, cur)} strong />
          </>
        )}
        {draft && <p className="text-xs text-fieldiq-muted">Tax and the total are set when the office approves the ticket.</p>}
      </dl>
    </PfPanel>
  );
}

function TotalRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-fieldiq-muted">{label}</dt>
      <dd className={`font-mono ${strong ? 'font-extrabold text-white' : 'text-fieldiq-text'}`}>{value}</dd>
    </div>
  );
}

